import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider, Tour, theme } from 'antd';
import { useSelector } from 'react-redux';
import { GUIDE_STEPS, usePromoGuide } from './PromoGuideContext';
import { useKeyboard } from '../Keyboard';

const STEP_CONTENT = {
    prizePool: {
        title: 'Set Amount & Token',
        description: (
            <>
                <div>Enter the prize pool amount.</div>
                <div>Choose any ETH token and paste the address.</div>
            </>
        ),
        placement: 'bottom',
    },
    categories: {
        title: 'Select reward categories',
        description: 'Choose 3 / 5 / 7 cat.',
        placement: 'bottom',
    },
    players: {
        title: 'Set the number of players',
        description: (
            <>
                <div>Enter how many players</div>
                <div>can join this promo.</div>
            </>
        ),
        placement: 'bottom',
    },
    winners: {
        title: 'Set the number of winners',
        description: (
            <>
                <div>Enter the number of winners</div>
                <div>for this promo.</div>
            </>
        ),
        placement: 'bottom',
    },
    tierWinners: {
        title: 'Set the winners for Each Tier',
        description: (
            <>
                <div>Enter the number of winners</div>
                <div>for each reward tier.</div>
            </>
        ),
        // Column target spans header→total; tip must sit under cells
        placement: 'bottom',
    },
    tierBudget: {
        title: 'Set the Prize Pool for Each Tier',
        description: (
            <>
                <div>Rewards per winner are calculated</div>
                <div>automatically.</div>
            </>
        ),
        placement: 'bottom',
    },
    telegramLink: {
        title: 'Set the Promoter link',
        description: (
            <>
                <div>Telegram only:</div>
                <div>channel / group / bot / account</div>
            </>
        ),
        placement: 'bottom',
    },
    promoTime: {
        title: 'Set the Promo Time',
        description: 'choose the Promo period',
        placement: 'bottom',
    },
    addTasks: {
        title: 'Add Tasks for Players',
        description: 'Create a task list for players.',
        placement: 'bottomLeft',
    },
};

/** Space kept free for Tour popover around the target */
const SPACE_TOP = 56;
const SPACE_BOTTOM = 140;
/** Extra bottom room so tip clears fixed Skip (bottom-[140px]) on cramped steps */
const SPACE_BOTTOM_SKIP_CLEAR = 280;
const SKIP_CLEAR_STEPS = new Set([
    'prizePool',
    'categories',
    'tierWinners',
    'tierBudget',
    'promoTime',
    'addTasks',
]);
const ACCORDION_MS = 260;
const SCROLL_DURATION_MS = 420;
const LAYOUT_SETTLE_MS = 80;

function guideTarget(stepId) {
    return () => document.querySelector(`[data-guide="${stepId}"]`);
}

function getScrollParent(el) {
    let node = el?.parentElement;
    while (node && node !== document.body) {
        const { overflowY } = getComputedStyle(node);
        if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight + 1) {
            return node;
        }
        node = node.parentElement;
    }
    return document.scrollingElement || document.documentElement;
}

function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function animateScroll(container, toTop, durationMs) {
    const from = container.scrollTop;
    const delta = toTop - from;
    if (Math.abs(delta) < 2) {
        container.scrollTop = toTop;
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        const start = performance.now();
        const tick = (now) => {
            const t = Math.min(1, (now - start) / durationMs);
            container.scrollTop = from + delta * easeInOutCubic(t);
            if (t < 1) {
                requestAnimationFrame(tick);
            } else {
                resolve();
            }
        };
        requestAnimationFrame(tick);
    });
}

/**
 * Smoothly scroll so the target sits with breathing room
 * for the Tour tip (space above/below).
 */
async function scrollGuideTargetIntoView(stepId) {
    const el = document.querySelector(`[data-guide="${stepId}"]`);
    if (!el) return;

    const placement = STEP_CONTENT[stepId]?.placement || 'bottom';
    const container = getScrollParent(el);
    const elRect = el.getBoundingClientRect();
    const box = container === document.documentElement || container === document.body
        ? { top: 0, height: window.innerHeight }
        : container.getBoundingClientRect();

    const viewTop = box.top + SPACE_TOP;
    const clearSkip = SKIP_CLEAR_STEPS.has(stepId);
    const tipRoom = clearSkip ? SPACE_BOTTOM_SKIP_CLEAR : SPACE_BOTTOM;
    const viewBottom = box.top + box.height - tipRoom;
    const elTop = elRect.top;
    const elBottom = elRect.bottom;

    let delta = 0;
    if (clearSkip) {
        // Force target higher so tip + fixed Skip don't overlap
        const desiredElBottom = box.top + box.height - tipRoom;
        delta = elBottom - desiredElBottom;
        if (elTop - delta < viewTop) {
            delta = elTop - viewTop;
        }
    } else if (placement === 'top') {
        // Tip above target — keep target a bit lower in the viewport
        const desiredTop = viewTop + (viewBottom - viewTop) * 0.45;
        if (elTop < viewTop || elBottom > viewBottom) {
            delta = elTop - desiredTop;
        }
    } else {
        // Tip below target — prefer fitting tip under the whole target
        // (important for tall column targets like tierWinners / tierBudget)
        const desiredElBottom = box.top + box.height - tipRoom;
        if (elBottom > desiredElBottom) {
            delta = elBottom - desiredElBottom;
        } else if (elTop < viewTop) {
            delta = elTop - viewTop;
        }
    }

    if (Math.abs(delta) < 4) return;

    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
    const nextTop = Math.max(0, Math.min(maxScroll, container.scrollTop + delta));
    await animateScroll(container, nextTop, SCROLL_DURATION_MS);
}

function unlockBodyScroll(prev) {
    const body = document.body;
    const html = document.documentElement;
    body.style.overflow = prev.bodyOverflow;
    body.style.touchAction = prev.bodyTouchAction;
    body.style.position = prev.bodyPosition;
    body.style.top = prev.bodyTop;
    body.style.left = prev.bodyLeft;
    body.style.right = prev.bodyRight;
    body.style.width = prev.bodyWidth;
    html.style.overscrollBehavior = prev.htmlOverscroll;
    window.scrollTo(0, prev.lockY);
}

function lockBodyScroll(lockY) {
    const body = document.body;
    const html = document.documentElement;
    const prev = {
        lockY,
        bodyOverflow: body.style.overflow,
        bodyTouchAction: body.style.touchAction,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyLeft: body.style.left,
        bodyRight: body.style.right,
        bodyWidth: body.style.width,
        htmlOverscroll: html.style.overscrollBehavior,
    };

    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    body.style.position = 'fixed';
    body.style.top = `-${lockY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    html.style.overscrollBehavior = 'none';
    return prev;
}

export default function PromoGuideTour() {
    const { step, skipGuide, advance } = usePromoGuide();
    const { isOpen: isKeyboardOpen } = useKeyboard();
    const [tourReady, setTourReady] = useState(false);
    const lockRef = useRef(null);

    // Discrete layout key for categories only (3 / 5 / 7) — no ResizeObserver loop
    const crystals = useSelector((s) => s.crystals?.items || []);
    const selectedId = useSelector((s) => s.crystals?.selectedId);
    const categoryNumber = useMemo(() => {
        const cat = crystals.find((c) => c.id === selectedId);
        return cat?.number || cat?.presets?.length || 0;
    }, [crystals, selectedId]);
    const layoutKey = step === 'categories' ? categoryNumber : 0;

    const current = step ? GUIDE_STEPS.indexOf(step) : -1;
    const canShow = Boolean(step) && !isKeyboardOpen && current >= 0;
    const open = canShow && tourReady;

    // Accordion → unlock → scroll for current cat height → re-lock → show Tour once
    useEffect(() => {
        if (!canShow) {
            setTourReady(false);
            return undefined;
        }

        let cancelled = false;
        setTourReady(false);

        const run = async () => {
            // Allow programmatic scroll while body is hard-locked
            if (lockRef.current) {
                unlockBodyScroll(lockRef.current);
                lockRef.current = null;
            }

            await new Promise((r) => setTimeout(r, ACCORDION_MS));
            if (cancelled) return;

            // Wait one paint so table minHeight for current cat is applied
            await new Promise((r) => requestAnimationFrame(() => r()));
            if (cancelled) return;

            await scrollGuideTargetIntoView(step);
            if (cancelled) return;

            await new Promise((r) => setTimeout(r, LAYOUT_SETTLE_MS));
            if (cancelled) return;

            lockRef.current = lockBodyScroll(window.scrollY);
            if (!cancelled) setTourReady(true);
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [step, canShow, layoutKey]);

    // Hard-lock page against finger/wheel scroll while guide is active.
    useEffect(() => {
        if (!canShow || typeof document === 'undefined') return undefined;

        const preventScroll = (e) => e.preventDefault();
        const keepLocked = () => {
            if (lockRef.current) window.scrollTo(0, lockRef.current.lockY);
        };

        document.addEventListener('touchmove', preventScroll, { passive: false });
        document.addEventListener('wheel', preventScroll, { passive: false });
        window.addEventListener('scroll', keepLocked);

        return () => {
            document.removeEventListener('touchmove', preventScroll);
            document.removeEventListener('wheel', preventScroll);
            window.removeEventListener('scroll', keepLocked);
            if (lockRef.current) {
                unlockBodyScroll(lockRef.current);
                lockRef.current = null;
            }
        };
    }, [canShow]);

    const steps = useMemo(
        () =>
            GUIDE_STEPS.map((id) => {
                const content = STEP_CONTENT[id] || { title: id, description: '' };
                const fullWidthTip =
                    id === 'categories'
                    || id === 'players'
                    || id === 'winners'
                    || id === 'tierWinners'
                    || id === 'tierBudget';
                return {
                    title: content.title,
                    description: content.description,
                    placement: content.placement || 'bottom',
                    target: guideTarget(id),
                    nextButtonProps: { style: { display: 'none' } },
                    prevButtonProps: { style: { display: 'none' } },
                    ...(fullWidthTip
                        ? {
                            styles: {
                                section: {
                                    width: 'calc(100vw - 32px)',
                                    maxWidth: '100%',
                                    ...((id === 'players' || id === 'tierWinners')
                                        ? { transform: 'translateX(12px)' }
                                        : {}),
                                },
                            },
                        }
                        : {}),
                    ...(id === 'addTasks'
                        ? {
                            styles: {
                                root: {
                                    // ~ left-3
                                    marginLeft: 12,
                                },
                            },
                        }
                        : {}),
                };
            }),
        []
    );

    return (
        <ConfigProvider
            theme={{
                algorithm: theme.darkAlgorithm,
                token: {
                    colorPrimary: '#2373BE',
                    borderRadius: 10,
                    fontSize: 13,
                },
                components: {
                    Tour: {
                        primaryColor: '#2373BE',
                        closeBtnSize: 16,
                    },
                },
            }}
        >
            <Tour
                key={`${step || 'closed'}-${layoutKey}`}
                open={open}
                current={Math.max(current, 0)}
                steps={steps}
                onClose={skipGuide}
                mask={{ color: 'rgba(0,0,0,0.6)' }}
                zIndex={70}
                animated
                scrollIntoViewOptions={false}
                styles={
                    step === 'addTasks'
                        ? {
                            root: {
                                // ~ left-3
                                marginLeft: 12,
                            },
                        }
                        : step === 'categories'
                        || step === 'players'
                        || step === 'winners'
                        || step === 'tierWinners'
                        || step === 'tierBudget'
                            ? {
                                section: {
                                    width: 'calc(100vw - 32px)',
                                    maxWidth: '100%',
                                    ...((step === 'players' || step === 'tierWinners')
                                        ? { transform: 'translateX(12px)' }
                                        : {}),
                                },
                            }
                            : undefined
                }
                indicatorsRender={(curr, total) => (
                    <span className="text-white/70 text-[12px] leading-none">
                        {curr + 1}/{total}
                    </span>
                )}
                actionsRender={(_, info) => (
                    <button
                        type="button"
                        onClick={advance}
                        className="h-[32px] px-3 rounded-[8px] bg-[#2373BE] border border-[#2373BE]
                            text-white text-[13px] font-semibold whitespace-nowrap active:scale-[0.97] transition-transform"
                    >
                        {info.current >= info.total - 1 ? 'Finish' : 'Next'}
                    </button>
                )}
            />
        </ConfigProvider>
    );
}
